// apps/web/src/features/russia-requests/components/tabs/CostTab.tsx
//
// #1548 lean-drawer cost section. Role-gated:
//   - logist_ru (`russiaCarriers.manage`) → the full editable carriers /
//     bids / winner-pick surface (reuses `CarriersTab` unchanged).
//   - everyone else (a Manager, lacking `russiaCarriers.read`) → a
//     READ-ONLY cost figure derived ONLY from the identity-free
//     `request.primaryCostSummary`.
//
// @aiSecurity THE PII BOUNDARY. The read-only branch renders ZERO carrier
//   identity (no name/contacts/id, no `primaryBidSummary` — which is null
//   for Managers anyway). The branch is selected purely on the permission
//   (`canSeeRussiaCarrierPii`), never on whether cost data is present.

import { useTranslation } from 'react-i18next';
import type { RussiaRequest, RussiaRequestPrimaryCostSummary } from '@magimp/shared';

import { useAuthStore } from '../../../../stores/auth.js';
import { canSeeRussiaCarrierPii } from '../../role.js';
import { CarriersTab } from './CarriersTab.js';

type UnitSuffixKey = `drawer.carriers.unitSuffix.${RussiaRequestPrimaryCostSummary['unit']}`;

const dtStyle = { fontWeight: 500, color: 'var(--fg-3)', fontSize: 13 };
const ddStyle = { margin: 0, fontSize: 13 };

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    // WHY: an unexpected currency code (legacy rows) must not crash the
    //   panel — fall back to a plain number plus the raw code.
    return `${price} ${currency}`;
  }
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
}

function CostReadOnly({ request }: { request: RussiaRequest }) {
  const { t } = useTranslation('russiaRequests');
  const summary = request.primaryCostSummary;

  if (summary === null) {
    return (
      <p
        data-testid="russia-request-drawer-cost-readonly-empty"
        style={{ color: 'var(--fg-3)', fontSize: 13, margin: 0 }}
      >
        {t('drawer.cost.readonly.empty')}
      </p>
    );
  }

  const unitSuffix = t(`drawer.carriers.unitSuffix.${summary.unit}` as UnitSuffixKey);
  return (
    <dl
      data-testid="russia-request-drawer-cost-readonly"
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        gap: '8px 16px',
        margin: 0,
      }}
    >
      <dt style={dtStyle}>{t('drawer.cost.readonly.price')}</dt>
      <dd style={ddStyle}>
        <strong
          data-testid="russia-request-drawer-cost-readonly-price"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatPrice(summary.price, summary.currency)}
          {unitSuffix}
        </strong>
      </dd>

      <dt style={dtStyle}>{t('drawer.cost.readonly.validityDays')}</dt>
      <dd style={ddStyle}>{summary.validityDays}</dd>

      <dt style={dtStyle}>{t('drawer.cost.readonly.expiresAt')}</dt>
      <dd style={ddStyle}>
        {formatDate(summary.expiresAt)}
        {summary.isExpired ? ` · ${t('drawer.cost.readonly.expired')}` : ''}
      </dd>
    </dl>
  );
}

export function CostTab({ request }: { request: RussiaRequest }) {
  const permissions = useAuthStore((s) => s.permissions);
  // INVARIANT (#1548 PII): branch purely on the permission, never on
  //   whether cost data is present, so a Manager never reaches the
  //   carrier-identity surface even when carriers exist.
  if (canSeeRussiaCarrierPii(permissions)) {
    return <CarriersTab request={request} />;
  }
  return (
    <div data-testid="russia-request-drawer-cost" style={{ display: 'grid', gap: 18 }}>
      <CostReadOnly request={request} />
    </div>
  );
}
